import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { getImageUrl } from '../utils/cdnUtils'
import { useWallet } from '../contexts/WalletContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import { useAuth } from '../contexts/AuthContext'
import { getPreferredUserIdentifier } from '../utils/userIdentity'
import {
  exchangePoints,
  getActivePointsRules,
  getPointsConfig,
  notifyUserSummaryUpdated,
  type ExchangeDirection,
  type PointsConfigData,
  type PointsRule,
} from '../services/pointsService'

type ClassicPointsMode = 'moneytopoint' | 'pointtomoney'

interface ClassicPointsExchangePageProps {
  mode: ClassicPointsMode
}

const getCurrentPoints = (userInfo: ReturnType<typeof useUserInfo>['userInfo']): number => {
  if (!userInfo) return 0
  const loyaltyPoints = Number(userInfo.loyaltyPoints?.currentPoints)
  if (Number.isFinite(loyaltyPoints)) return loyaltyPoints
  const bonusPoints = Number((Array.isArray(userInfo.bonuses) ? userInfo.bonuses[0]?.points : 0) || 0)
  return Number.isFinite(bonusPoints) ? bonusPoints : 0
}

const PAGE_CONFIG: Record<ClassicPointsMode, {
  title: string
  subtitle: string
  direction: ExchangeDirection
  emptyMessage: string
  exchangeDisabledMessage: string
  confirmTitle: string
  successTitle: string
  failureTitle: string
  confirmHtml: (rule: PointsRule, formatCurrency: (amount: number) => string) => string
  cardMainText: (rule: PointsRule, formatCurrency: (amount: number) => string) => string
  cardSecondaryText: (rule: PointsRule, formatCurrency: (amount: number) => string) => string
  buttonText: string
}> = {
  moneytopoint: {
    title: 'แลกเปลี่ยนเงินเป็นคะแนน',
    subtitle: 'เปลี่ยนเครดิตในกระเป๋าเป็นคะแนนสะสม เพื่อใช้ในสิทธิประโยชน์ของระบบคะแนน',
    direction: 'cash-to-points',
    emptyMessage: 'ขณะนี้ยังไม่มีแพ็กเกจแลกเงินเป็นคะแนนที่เปิดใช้งาน',
    exchangeDisabledMessage: 'ปิดระบบแลกเงินเป็นคะแนน',
    confirmTitle: 'ยืนยันการแลกเงินเป็นคะแนน',
    successTitle: 'แลกเงินเป็นคะแนนสำเร็จ',
    failureTitle: 'แลกเงินเป็นคะแนนไม่สำเร็จ',
    confirmHtml: (rule, formatCurrency) => `ใช้เครดิต <strong>${formatCurrency(rule.inputAmount)}</strong> บาท เพื่อรับ <strong>${rule.outputAmount.toLocaleString('th-TH')}</strong> คะแนน`,
    cardMainText: (rule, formatCurrency) => `${formatCurrency(rule.inputAmount)} บาท`,
    cardSecondaryText: (rule) => `รับ ${rule.outputAmount.toLocaleString('th-TH')} คะแนน`,
    buttonText: 'แลกคะแนน',
  },
  pointtomoney: {
    title: 'แลกเปลี่ยนคะแนนเป็นเงิน',
    subtitle: 'นำคะแนนสะสมกลับมาเป็นเครดิตในกระเป๋า เมื่อมีแพ็กเกจที่เปิดใช้งาน',
    direction: 'points-to-cash',
    emptyMessage: 'ขณะนี้ยังไม่มีแพ็กเกจแลกคะแนนเป็นเงินที่เปิดใช้งาน',
    exchangeDisabledMessage: 'ปิดระบบแลกคะแนนเป็นเงิน',
    confirmTitle: 'ยืนยันการแลกคะแนนเป็นเงิน',
    successTitle: 'แลกคะแนนเป็นเงินสำเร็จ',
    failureTitle: 'แลกคะแนนเป็นเงินไม่สำเร็จ',
    confirmHtml: (rule) => `ใช้ <strong>${rule.inputAmount.toLocaleString('th-TH')}</strong> คะแนน เพื่อรับเครดิต <strong>${rule.outputAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> บาท`,
    cardMainText: (rule) => `${rule.inputAmount.toLocaleString('th-TH')} คะแนน`,
    cardSecondaryText: (rule, formatCurrency) => `รับ ${formatCurrency(rule.outputAmount)} บาท`,
    buttonText: 'แลกเป็นเงิน',
  },
}

const ClassicPointsExchangePage: React.FC<ClassicPointsExchangePageProps> = ({ mode }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { walletData, formatCurrency, refreshWallet } = useWallet()
  const { userInfo, refreshUserInfo } = useUserInfo()
  const [pointsConfig, setPointsConfig] = useState<PointsConfigData | null>(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [submittingRuleId, setSubmittingRuleId] = useState<string | null>(null)

  const tenantId = user?.tenantId || userInfo?.tenantId || ''
  const config = PAGE_CONFIG[mode]
  const points = getCurrentPoints(userInfo)
  const pointsText = `${points.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pts`
  const balanceText = walletData ? `${formatCurrency(walletData.balance.main)} บาท` : '0.00 บาท'

  const activeRules = useMemo(
    () => getActivePointsRules(
      mode === 'moneytopoint' ? pointsConfig?.cashToPointsRules : pointsConfig?.pointsToCashRules
    ),
    [mode, pointsConfig]
  )

  const exchangeEnabled = mode === 'moneytopoint'
    ? pointsConfig?.system?.cashToPointsEnabled === true
    : pointsConfig?.system?.pointsToCashEnabled === true

  const rewardsEnabled = pointsConfig?.system?.rewardsEnabled !== false

  useEffect(() => {
    let active = true

    const loadConfig = async () => {
      if (!tenantId) {
        setPointsConfig(null)
        setIsLoadingConfig(false)
        return
      }

      try {
        setIsLoadingConfig(true)
        const nextConfig = await getPointsConfig(tenantId)
        if (active) {
          setPointsConfig(nextConfig)
        }
      } finally {
        if (active) {
          setIsLoadingConfig(false)
        }
      }
    }

    void loadConfig()
    return () => {
      active = false
    }
  }, [tenantId])

  const handleExchange = async (ruleId: string) => {
    const targetUserId = getPreferredUserIdentifier(user)
    const selectedRule = activeRules.find((rule) => rule.id === ruleId)

    if (!tenantId || !targetUserId || !selectedRule) {
      await Swal.fire({
        icon: 'error',
        title: 'ไม่พบข้อมูลผู้ใช้',
        text: 'กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง',
      })
      return
    }

    const confirmed = await Swal.fire({
      icon: 'question',
      title: config.confirmTitle,
      html: config.confirmHtml(selectedRule, formatCurrency),
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#dc3545',
      reverseButtons: true,
    })

    if (!confirmed.isConfirmed) return

    try {
      setSubmittingRuleId(ruleId)
      const response = await exchangePoints({
        tenantId,
        userId: targetUserId,
        direction: config.direction,
        ruleId,
      })

      notifyUserSummaryUpdated()
      await Promise.all([refreshWallet(), refreshUserInfo()])

      await Swal.fire({
        icon: 'success',
        title: response.message || config.successTitle,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#28a745',
      })
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: config.failureTitle,
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonText: 'ตกลง',
      })
    } finally {
      setSubmittingRuleId(null)
    }
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/dashboard')
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าหลัก
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-coins me-2"></i>
                      {config.title}
                    </div>

                    <div className="bgc-4 bdr-5 p-2 mb-2 text-center">
                      <p className="c-dark mb-1">ระบบคะแนนสะสม</p>
                      <small className="c-dark">{config.subtitle}</small>
                    </div>

                    <div className="row mx--1">
                      <div className="col-12 px-1 mb-2">
                        <div className="nav nav-pills nav-pills-ticket mx--1" role="tablist">
                          <div className={`nav-link col px-1 py-0 ${mode === 'moneytopoint' ? 'active' : ''}`}>
                            <Link
                              to="/points/moneytopoint"
                              className={`check-button w-100 d-block text-decoration-none ${
                                mode === 'moneytopoint' ? 'bgg-5 c-white' : 'bgc-4 c-dark'
                              }`}
                            >
                              <h4><i className="fas fa-wallet"></i></h4>
                              <p>เงินเป็นคะแนน</p>
                            </Link>
                          </div>
                          <div className={`nav-link col px-1 py-0 ${mode === 'pointtomoney' ? 'active' : ''}`}>
                            <Link
                              to="/points/pointtomoney"
                              className={`check-button w-100 d-block text-decoration-none ${
                                mode === 'pointtomoney' ? 'bgg-5 c-white' : 'bgc-4 c-dark'
                              }`}
                            >
                              <h4><i className="fas fa-exchange-alt"></i></h4>
                              <p>คะแนนเป็นเงิน</p>
                            </Link>
                          </div>
                          {rewardsEnabled && (
                            <div className="nav-link col px-1 py-0">
                              <Link
                                to="/points/rewards"
                                className="check-button w-100 d-block text-decoration-none bgc-4 c-dark"
                              >
                                <h4><i className="fas fa-gift"></i></h4>
                                <p>ของรางวัล</p>
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 px-1 mb-2">
                        <div className="bgc-4 bdr-5 p-3 text-center">
                          <p className="c-dark mb-2">คะแนนสะสมปัจจุบัน</p>
                          <h2 className="c-red t-medium mb-0">{pointsText}</h2>
                        </div>
                      </div>

                      <div className="col-12 col-md-4 px-1 mb-2">
                        <div className="bgc-4 bdr-5 p-2 h-100 text-center">
                          <p className="c-dark mb-1">เครดิตคงเหลือ</p>
                          <h5 className="c-main t-medium mb-0">{balanceText}</h5>
                        </div>
                      </div>
                      <div className="col-12 col-md-4 px-1 mb-2">
                        <div className="bgc-4 bdr-5 p-2 h-100 text-center">
                          <p className="c-dark mb-1">สถานะระบบแลกคะแนน</p>
                          <h6 className={`mb-0 t-medium ${exchangeEnabled && activeRules.length > 0 ? 'c-green' : 'c-red'}`}>
                            {isLoadingConfig
                              ? 'กำลังโหลด...'
                              : exchangeEnabled && activeRules.length > 0
                                ? 'พร้อมใช้งาน'
                                : exchangeEnabled
                                  ? 'ยังไม่มีแพ็กเกจที่เปิดใช้งาน'
                                  : config.exchangeDisabledMessage}
                          </h6>
                        </div>
                      </div>
                      <div className="col-12 col-md-4 px-1 mb-2">
                        <div className="bgc-4 bdr-5 p-2 h-100 text-center">
                          <p className="c-dark mb-1">จำนวนแพ็กเกจ</p>
                          <h5 className="c-red t-medium mb-0">{isLoadingConfig ? '...' : activeRules.length}</h5>
                        </div>
                      </div>
                    </div>

                    <div className="bgc-4 bdr-5 p-2 mb-2">
                      <p className="c-dark mb-1">คำอธิบาย</p>
                      <small className="c-dark">
                        {mode === 'moneytopoint'
                          ? 'เลือกแพ็กเกจเพื่อเปลี่ยนเครดิตเป็นคะแนนสะสม คะแนนจะอัปเดตทันทีหลังทำรายการสำเร็จ'
                          : 'เลือกแพ็กเกจเพื่อเปลี่ยนคะแนนสะสมกลับเป็นเครดิตในกระเป๋า ระบบจะอัปเดตยอดให้อัตโนมัติหลังทำรายการสำเร็จ'}
                      </small>
                    </div>

                    {exchangeEnabled && activeRules.length > 0 ? (
                      <div className="row mx--1">
                        {activeRules.map((rule) => (
                          <div key={rule.id} className="col-12 col-md-6 px-1 mb-2">
                            <div className="bgc-4 bdr-5 p-2 h-100 d-flex flex-column">
                              <div className="wrap align-items-center mb-2">
                                <div className="img-b bdr-5 bgc-7">
                                  <img src={getImageUrl('logo-m.png')} alt="" />
                                </div>
                                <div className="txt-b ms-3">
                                  <h6 className="c-dark mb-1">{rule.name || 'แพ็กเกจแลกคะแนน'}</h6>
                                  <h5 className="c-red t-medium mb-1">
                                    {config.cardMainText(rule, formatCurrency)}
                                  </h5>
                                  <p className="c-dark fs-14 mb-0">
                                    {config.cardSecondaryText(rule, formatCurrency)}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                className={`check-button w-100 border-0 c-white mt-auto ${mode === 'moneytopoint' ? 'bgg-5' : 'bgg-1'}`}
                                onClick={() => void handleExchange(rule.id)}
                                disabled={submittingRuleId === rule.id}
                              >
                                <i className="fas fa-sync-alt me-2"></i>
                                {submittingRuleId === rule.id ? 'กำลังทำรายการ...' : config.buttonText}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bgc-4 bdr-5 p-3 text-center">
                        <h6 className="c-dark mb-1">ยังไม่พร้อมใช้งาน</h6>
                        <p className="c-dark mb-0">
                          {isLoadingConfig ? 'กำลังโหลดรายการแลกคะแนน...' : config.emptyMessage}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClassicPointsExchangePage
